<template>
    <div class="export-csv">
        <div class="calendar-button" v-bind:class="{ 'calendar-button--open': isOpen || isRunning }"
            v-bind:title="$t('address.export.title')" v-on:click="isOpen = !isOpen">
            <div class="calendar-button__main">
                <span class="calendar-button__icon">
                    <svg v-if="isRunning" class="export-csv__spinner" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                        <circle v-pre cx="9" cy="9" fill="none" stroke="currentColor" stroke-width="2" r="8" stroke-dasharray="34 12">
                            <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="1s" values="0 9 9;360 9 9" keyTimes="0;1"/>
                        </circle>
                    </svg>
                    <icon-download v-else />
                </span>
            </div>
        </div>

        <div class="card export-csv__menu" v-if="isOpen">
            <div class="export-csv__title" v-text="$t('address.export.title')" />
            <div class="export-csv__text" v-text="$t('address.export.description')" />

            <div class="export-csv__status export-csv__status--running" v-if="isRunning">
                <svg class="export-csv__spinner" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                    <circle v-pre cx="9" cy="9" fill="none" stroke="currentColor" stroke-width="2" r="8" stroke-dasharray="34 12">
                        <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="1s" values="0 9 9;360 9 9" keyTimes="0;1"/>
                    </circle>
                </svg>
                <div>
                    <div v-text="$t('address.export.progress', { rows: rows.toLocaleString($i18n.locale) })" />
                    <div class="export-csv__muted" v-if="oldest" v-text="$t('address.export.oldest', { date: oldest })" />
                </div>
            </div>
            <div class="export-csv__status export-csv__muted" v-else-if="failed" v-text="$t('address.export.failed')" />

            <button class="export-csv__start" v-bind:class="{ 'export-csv__start--stop': isRunning }" v-on:click="toggle"
                v-text="isRunning ? $t('address.export.stop') : $t('address.export.start')" />
        </div>

        <div class="calendar-overlay" v-if="isOpen" v-on:click="isOpen = false" />
    </div>
</template>

<script>
import IconDownload from '@img/icons/lucide/cloud-download.svg?vue';
import { csvExport, startCsvExport, stopCsvExport, cancelCsvExport } from '~/addressCsvExport.js';

export default {
    props: {
        address: { type: String, required: true },
    },

    data() {
        return { isOpen: false };
    },

    watch: {
        // The export belongs to the address it was started on: going to another one drops it.
        address: {
            handler() {
                if (csvExport.address !== null && csvExport.address !== this.address) cancelCsvExport();
            },
            immediate: true,
        },
    },

    computed: {
        isRunning() {
            return csvExport.address !== null;
        },
        rows() {
            return csvExport.rows;
        },
        failed() {
            return csvExport.failed;
        },
        oldest() {
            return csvExport.oldest && new Date(csvExport.oldest).toLocaleDateString(this.$i18n.locale);
        },
    },

    methods: {
        toggle() {
            if (this.isRunning) {
                stopCsvExport();
            } else {
                startCsvExport(this.address);
            }
        },
    },

    components: { IconDownload },
};
</script>

<style lang="scss">
.export-csv {
    position: relative;
    display: flex;
    align-items: center;
    padding-left: 10px;

    &__spinner {
        width: 18px;
        height: 18px;
    }

    // Same card as the date picker and filters next to it (CalendarMenu.vue, FilterMenu.vue):
    &__menu {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        width: 320px;
        box-sizing: border-box;
        padding: 14px 14px 16px;
        z-index: 90001;
        cursor: default;
        box-shadow: 0 1px 2px var(--card-box-shadow-color),
                    0 16px 40px -12px rgba(0, 0, 0, .35);
        transform-origin: top right;
        animation: export-csv-in .16s cubic-bezier(.2, .8, .2, 1);
    }

    &__title {
        margin: 0 2px 8px;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: -0.01em;
    }

    &__text {
        margin: 0 2px;
        font-size: 13px;
        line-height: 1.45;
        color: var(--body-muted-text-color);
    }

    &__status {
        margin-top: 16px;
        padding: 10px 12px;
        border-radius: 12px;
        font-size: 13px;
        line-height: 1.5;
        background: var(--modal-dark-background);

        &--running {
            display: flex;
            align-items: center;
            gap: 12px;

            .export-csv__spinner {
                flex-shrink: 0;
                color: var(--blue-bright);
            }
        }
    }

    &__muted {
        color: var(--body-muted-text-color);
    }

    &__start {
        display: block;
        width: 100%;
        height: 44px;
        margin-top: 16px;
        border: none;
        border-radius: 12px;
        font: inherit;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        color: #fff;
        background: var(--blue-bright);
        transition: opacity .15s ease;

        &:hover {
            opacity: .9;
        }
        &:active {
            opacity: .8;
        }

        &--stop {
            color: var(--body-text-color);
            background: var(--body-light-muted-color);
        }
    }
}

@keyframes export-csv-in {
    from {
        opacity: 0;
        transform: translateY(-4px) scale(.98);
    }
}

@media (prefers-reduced-motion: reduce) {
    .export-csv__menu {
        animation: none;
    }
}
</style>
